import { useLocation, useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { TextField, Grid, Box, Typography } from "@mui/material";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import useDistributor from "hooks/useDistributor";

const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/jpg"];

const DistributorForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const distributorId = queryParams.get("id");
  const { createdistributor, getdistributorById, updatedistributor, loading } = useDistributor();
  const [imageError, setImageError] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    primaryPhoneNo: "",
    secondaryPhoneNo: "",
    email: "",
    warehouse: [
      {
        street: "",
        city: "",
        state: "",
        country: "",
        pincode: "",
        isMain: true,
      },
    ],
    image: null,
    imagePreview: null,
  });

  const validateImage = (file) => {
    if (!file) return false;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setImageError("Only JPG, JPEG, and PNG formats are allowed.");
      return false;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setImageError("File size should be under 5MB.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert("Name is required.");
      return;
    }
    if (!formData.primaryPhoneNo.trim()) {
      alert("Primary Phone Number is required.");
      return;
    }
    if (!/^[+0-9]{10,15}$/.test(formData.primaryPhoneNo)) {
      alert("Enter a valid phone number.");
      return;
    }
    if (!formData.email.trim()) {
      alert("Email is required.");
      return;
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      alert("Enter a valid email.");
      return;
    }

    const formDataToSubmit = new FormData();
    formDataToSubmit.append("name", formData.name);
    formDataToSubmit.append("primaryPhoneNo", formData.primaryPhoneNo);
    formDataToSubmit.append("secondaryPhoneNo", formData.secondaryPhoneNo);
    formDataToSubmit.append("email", formData.email);
    formData.warehouse.forEach((warehouse, index) => {
      Object.keys(warehouse).forEach((key) => {
        formDataToSubmit.append(`warehouse[${index}][${key}]`, warehouse[key]);
      });
    });
    if (formData.image) {
      formDataToSubmit.append("image", formData.image);
    }

    if (distributorId) {
      const updateResponse = await updatedistributor(distributorId, formDataToSubmit);
      if (updateResponse?.success) {
        navigate("/distributor-management");
      } else {
        alert(updateResponse?.message);
      }
      return;
    }

    const createResponse = await createdistributor(formDataToSubmit);
    if (createResponse?.success) {
      setFormData({
        name: "",
        primaryPhoneNo: "",
        secondaryPhoneNo: "",
        email: "",
        warehouse: [],
        image: null,
        imagePreview: null,
      });
      navigate("/distributor-management");
    } else {
      alert(createResponse?.message);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleWarehouseChange = (index, field, value) => {
    const updatedWarehouses = [...formData.warehouse];
    updatedWarehouses[index][field] = value;
    setFormData((prevState) => ({ ...prevState, warehouse: updatedWarehouses }));
  };

  const addWarehouse = () => {
    const lastWarehouse = formData.warehouse[formData.warehouse.length - 1];
    if (lastWarehouse && Object.values(lastWarehouse).some((value) => value === "")) {
      alert("Please fill all fields in the last warehouse before adding a new one.");
      return;
    }

    setFormData((prevState) => ({
      ...prevState,
      warehouse: [
        ...prevState.warehouse,
        { street: "", city: "", state: "", country: "", pincode: "", isMain: false },
      ],
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (!validateImage(file)) {
      setFormData((prev) => ({
        ...prev,
        image: null,
        imagePreview: null,
      }));
      return;
    }

    setImageError(""); // Clear previous errors if valid
    setFormData((prev) => ({
      ...prev,
      image: file,
      imagePreview: URL.createObjectURL(file),
    }));
  };

  const removeWarehouse = (index) => {
    const updatedWarehouses = formData.warehouse.filter((_, i) => i !== index);
    setFormData((prevState) => ({ ...prevState, warehouse: updatedWarehouses }));
  };

  const fetchDistributor = async () => {
    const response = await getdistributorById(distributorId);
    if (response?.success) {
      setFormData({
        name: response.data.name,
        image: response.data.image,
        primaryPhoneNo: response.data.primaryPhoneNo,
        secondaryPhoneNo: response.data.secondaryPhoneNo,
        email: response.data.email,
        imagePreview: response.data.image,
        warehouse: response.data.warehouse,
      });
    }
  };

  useEffect(() => {
    if (distributorId) {
      fetchDistributor();
    }
  }, [distributorId]);

  return (
    <Box
      sx={{
        width: "100%",
        background: "white",
        minHeight: `calc(100vh - 120px)`,
        p: 4,
        borderRadius: 2,
        boxShadow: 3,
      }}
    >
      <MDTypography variant="h5" component="h1" align="center" gutterBottom>
        Distributor Form
      </MDTypography>
      <MDTypography variant="caption" color="text" align="center" paragraph>
        Fill out the details below to register/update Distributor.
      </MDTypography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Name*
          </MDTypography>
          <TextField
            fullWidth
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Primary Phone Number*
          </MDTypography>
          <TextField
            fullWidth
            name="primaryPhoneNo"
            value={formData.primaryPhoneNo}
            onChange={handleChange}
            required
            variant="outlined"
            type="number"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Secondary Phone Number
          </MDTypography>
          <TextField
            fullWidth
            name="secondaryPhoneNo"
            value={formData.secondaryPhoneNo}
            onChange={handleChange}
            variant="outlined"
            type="number"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Email*
          </MDTypography>
          <TextField
            fullWidth
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            variant="outlined"
          />
        </Grid>
        {/* Image Upload */}
        <Grid item xs={12} md={12}>
          <MDTypography variant="body2" gutterBottom>
            Image* <span style={{ fontSize: "10px" }}>(JPG, JPEG, PNG - Max 5MB)</span>
          </MDTypography>
          <input
            accept="image/*"
            type="file"
            name="image"
            onChange={handleImageChange}
            style={{ display: "none" }}
            id="image-file-upload"
          />
          <label htmlFor="image-file-upload">
            <MDButton
              fullWidth
              variant="contained"
              color="info"
              component="span"
              sx={{
                padding: "8px 20px",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "#0056b3",
                },
              }}
            >
              Upload Image
            </MDButton>
          </label>
          {imageError && (
            <Typography color="error" variant="caption" sx={{ mt: 1 }}>
              {imageError}
            </Typography>
          )}
        </Grid>

        {/* Image Preview */}
        <Grid item xs={12}>
          {formData.imagePreview && (
            <Box
              sx={{
                mt: 2,
                width: "100%",
                height: "200px",
                border: "2px dashed #ccc",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "8px",
                overflow: "hidden",
                position: "relative",
              }}
            >
              <img
                src={formData.imagePreview}
                alt="Preview"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain", // Ensures the entire image fits within the box
                  borderRadius: "8px",
                }}
              />
              <MDButton
                size="small"
                variant="contained"
                color="error"
                sx={{ position: "absolute", top: 8, right: 8 }}
                onClick={() =>
                  setFormData((prev) => ({ ...prev, image: null, imagePreview: null }))
                }
              >
                Remove
              </MDButton>
            </Box>
          )}
        </Grid>
        {formData.warehouse.map((warehouse, index) => (
          <React.Fragment key={index}>
            <Grid item xs={12} md={6}>
              <MDTypography variant="body2" gutterBottom>
                Warehouse {index + 1} Street*
              </MDTypography>
              <TextField
                fullWidth
                value={warehouse.street}
                onChange={(e) => handleWarehouseChange(index, "street", e.target.value)}
                required
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MDTypography variant="body2" gutterBottom>
                Warehouse {index + 1} City*
              </MDTypography>
              <TextField
                fullWidth
                value={warehouse.city}
                onChange={(e) => handleWarehouseChange(index, "city", e.target.value)}
                required
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MDTypography variant="body2" gutterBottom>
                Warehouse {index + 1} State*
              </MDTypography>
              <TextField
                fullWidth
                value={warehouse.state}
                onChange={(e) => handleWarehouseChange(index, "state", e.target.value)}
                required
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MDTypography variant="body2" gutterBottom>
                Warehouse {index + 1} Country*
              </MDTypography>
              <TextField
                fullWidth
                value={warehouse.country}
                onChange={(e) => handleWarehouseChange(index, "country", e.target.value)}
                required
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MDTypography variant="body2" gutterBottom>
                Warehouse {index + 1} Pincode*
              </MDTypography>
              <TextField
                fullWidth
                value={warehouse.pincode}
                onChange={(e) => handleWarehouseChange(index, "pincode", e.target.value)}
                required
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MDButton variant="outlined" color="secondary" onClick={() => removeWarehouse(index)}>
                Remove Warehouse
              </MDButton>
            </Grid>
          </React.Fragment>
        ))}
        <Grid item xs={12}>
          <MDButton variant="contained" color="primary" onClick={addWarehouse}>
            Add Warehouse
          </MDButton>
        </Grid>
        {/* Submit Button */}
        <Grid item xs={12}>
          <MDBox>
            <MDButton
              type="submit"
              variant="contained"
              color="dark"
              fullWidth
              onClick={handleSubmit}
              sx={{
                marginTop: "3rem",
                padding: "10px 20px",
                borderRadius: "12px",
                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
              }}
            >
              {loading ? "Submitting..." : distributorId ? "Update Distributor" : "Submit"}
            </MDButton>
          </MDBox>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DistributorForm;
